import React, {useState, useCallback} from 'react';
import {TextField} from '@mui/material';
import {removeInitialSpecialChars} from '../../../../util/sanitizeUtil';
import {validateContainerNumber, getContainerNumberErrorMessage} from '../../../../util/ContainerValidator';
import {CheckInFieldProps} from '../interfaces';
import {CODE_INPUT_PROPS} from './keyboardInputProps';

/**
 * Container Number text field with built-in input transformations and inline validation.
 * Strips spaces, uppercases, sanitizes on every keystroke.
 * Runs container-number format validation inline and displays its own error state.
 * The parent's `error` prop (from submit-time validation) is also displayed when present.
 */
const ContainerNumberField: React.FC<CheckInFieldProps> = ({value, onValueChange, error, required}) => {
  const [inlineError, setInlineError] = useState<string | undefined>();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = removeInitialSpecialChars(e.target.value.toUpperCase()).replace(/ /g, '');

    if (sanitized && !validateContainerNumber(sanitized)) {
      const msgs = getContainerNumberErrorMessage(sanitized);
      setInlineError(msgs ? (msgs as string[])[0] : 'Container number is invalid.');
    } else {
      setInlineError(undefined);
    }

    onValueChange('containerNumber', sanitized);
  }, [onValueChange]);

  const displayError = error || inlineError;

  return (
    <TextField
      id="containerNumber"
      name="containerNumber"
      label="Container Number"
      value={value}
      onChange={handleChange}
      placeholder="Enter a valid container number"
      variant="standard"
      fullWidth
      required={required}
      error={!!displayError}
      helperText={displayError}
      inputProps={{...CODE_INPUT_PROPS, 'aria-label': 'Container Number'}}
    />
  );
};

export default ContainerNumberField;

